/**
 * Deal Health tab — the scored open-pipeline landscape.
 *
 * Layout: band KPI tiles (click to filter) → landscape bubble chart with the
 * per-deal radar detail beside it → all-deals table. Everything renders from
 * one /cro_forecast/deal-health payload; scores are computed server-side
 * against the WHOLE open pipeline, so band filtering here never re-ranks
 * anyone (a filtered view shows a subset, not a re-scored cohort).
 */
import { useSearch } from "@tanstack/react-router";
import { Info, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";

import { DateRangePicker, type DateRange } from "@/components/cinch/DatePicker";
import {
  ActiveFilterPill,
  CheckboxList,
  FilterBar as DsFilterBar,
  FilterChip,
  FilterClearAll,
} from "@/components/cinch/FilterBar";
import { KpiTile } from "@/components/cinch/KpiTile";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  useDealHealth,
  useRefreshDealHealth,
  type DealHealthBand,
  type DealHealthDeal,
} from "../api";
import { CroForecastFilterBar } from "../components/FilterBar";
import { CroForecastSubNav } from "../components/SubNav";
import { DealHealthDetail } from "../components/DealHealthDetail";
import { DealHealthTable } from "../components/DealHealthTable";
import { DealLandscape } from "../components/DealLandscape";
import { pipelineShort } from "../components/dealHealthColors";
import { stageOptionsFor } from "../components/dealHealthFilters";
import { fmtCents, fmtCount } from "../format";
import type { CroForecastSearch } from "../search";

const fmtYmd = (d: Date | null) => (d ? d.toISOString().slice(0, 10) : null);

/** Inclusive YYYY-MM-DD range check against an ISO date/timestamp string. */
function inRange(iso: string | null, range: DateRange): boolean {
  const from = fmtYmd(range.start);
  const to = fmtYmd(range.end);
  if (!from && !to) return true;
  if (!iso) return false; // a bounded filter excludes deals missing the date
  const ymd = iso.slice(0, 10);
  if (from && ymd < from) return false;
  if (to && ymd > to) return false;
  return true;
}

const rangeLabel = (range: DateRange) =>
  range.start || range.end
    ? `${fmtYmd(range.start) ?? "…"} → ${fmtYmd(range.end) ?? "…"}`
    : null;

/** "Aug 4, 2:31 PM" in the viewer's local time — the footer's refresh
 *  stamp needs the time of day now that a Refresh button exists. */
function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function CroForecastDealHealthPage() {
  const search = useSearch({
    from: "/cro-forecast/deal-health",
  }) as CroForecastSearch;
  const { data, isLoading, error } = useDealHealth();
  const refresh = useRefreshDealHealth();

  const [band, setBand] = useState<DealHealthBand | null>(null);
  const [pipeline, setPipeline] = useState<string | null>(null);
  const [selected, setSelected] = useState<DealHealthDeal | null>(null);

  // DS FilterBar state — kept local like Open Deals (the shared date-week
  // params already live in the URL; these are per-visit refinements).
  const [ownerNames, setOwnerNames] = useState<string[]>([]);
  const [stageNames, setStageNames] = useState<string[]>([]);
  const [createdRange, setCreatedRange] = useState<DateRange>({
    start: null,
    end: null,
  });
  const [closeRange, setCloseRange] = useState<DateRange>({
    start: null,
    end: null,
  });

  const ownerOptions = useMemo(() => {
    const owners = new Set<string>();
    for (const d of data?.deals ?? []) {
      if (d.owner_display_name) owners.add(d.owner_display_name);
    }
    return [...owners].sort();
  }, [data]);

  // Stage options (funnel order, live rungs only, shared names untagged) —
  // see stageOptionsFor for the rules.
  const stageOptions = useMemo(
    () => stageOptionsFor(data, pipeline),
    [data, pipeline],
  );

  // Switching pipeline can orphan stage selections. Dropping them beats
  // leaving an invisible filter that empties the view with no visible cause.
  const validStages = useMemo(
    () => new Set(stageOptions.map((o) => o.value)),
    [stageOptions],
  );
  const activeStages = useMemo(
    () => stageNames.filter((s) => validStages.has(s)),
    [stageNames, validStages],
  );

  // Everything except the band filter — the band tiles count within this
  // set so tiles, chart, and table always agree.
  const base = useMemo(() => {
    return (data?.deals ?? []).filter((d) => {
      if (pipeline && d.pipeline_name !== pipeline) return false;
      if (
        ownerNames.length > 0 &&
        !ownerNames.includes(d.owner_display_name ?? "")
      )
        return false;
      if (activeStages.length > 0 && !activeStages.includes(d.stage_name ?? ""))
        return false;
      if (!inRange(d.created_at, createdRange)) return false;
      if (!inRange(d.close_date, closeRange)) return false;
      return true;
    });
  }, [data, pipeline, ownerNames, activeStages, createdRange, closeRange]);

  const deals = useMemo(
    () => (band ? base.filter((d) => d.band === band) : base),
    [base, band],
  );

  const tileCounts = useMemo(() => {
    const counts = { healthy: 0, at_risk: 0, stalled: 0, cents: 0 };
    for (const d of base) {
      counts[d.band] += 1;
      counts.cents += d.amount_cents;
    }
    return { ...counts, total: base.length };
  }, [base]);

  const toggleBand = (b: DealHealthBand) => setBand(band === b ? null : b);
  const togglePipeline = (p: string) => setPipeline(pipeline === p ? null : p);

  const hasAnyFilter =
    ownerNames.length > 0 ||
    activeStages.length > 0 ||
    createdRange.start != null ||
    createdRange.end != null ||
    closeRange.start != null ||
    closeRange.end != null;

  const clearAll = () => {
    setOwnerNames([]);
    setStageNames([]);
    setCreatedRange({ start: null, end: null });
    setCloseRange({ start: null, end: null });
  };

  const createdLabel = rangeLabel(createdRange);
  const closeLabel = rangeLabel(closeRange);

  return (
    <div className="flex flex-col">
      <CroForecastFilterBar search={search} />
      <CroForecastSubNav search={search} />

      <div className="flex flex-col gap-4 p-4 md:p-6">
        {isLoading && (
          <p className="text-sm text-muted-foreground">Scoring open deals…</p>
        )}
        {error && (
          <p className="text-sm text-destructive">
            Failed to load: {(error as Error).message}
          </p>
        )}
        {data && (
          <>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={pipeline === null ? "default" : "outline"}
                size="sm"
                onClick={() => setPipeline(null)}
              >
                All pipelines
              </Button>
              {data.pipelines.map((p) => (
                <Button
                  key={p}
                  variant={pipeline === p ? "default" : "outline"}
                  size="sm"
                  onClick={() => togglePipeline(p)}
                >
                  {pipelineShort(p)}
                </Button>
              ))}

              {/* Right edge: how current the HubSpot data is, then a button
                  that bypasses the 4h server-side cache and re-pulls from
                  Snowflake. Refresh can't outrun the nightly mart, which is
                  why the stamp shows the mart's newest edit, not the pull. */}
              <TooltipProvider delayDuration={250}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="ml-auto inline-flex cursor-help items-center gap-1 self-center rounded-sm text-xs text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Info className="h-3.5 w-3.5" aria-hidden />
                      {data.data_as_of
                        ? `Nightly Snowflake HubSpot data · as of ${fmtDateTime(
                            data.data_as_of,
                          )}`
                        : "Nightly Snowflake HubSpot data · age unknown"}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs text-xs">
                    <p>
                      This tab reads HubSpot through Cinch&apos;s Snowflake data
                      mart, which rebuilds once a night. It is not a live view
                      of HubSpot.
                    </p>
                    <p className="mt-1.5">
                      The time shown is the newest HubSpot deal change the mart
                      has picked up. Anything edited in HubSpot after that (a
                      deal moved to Closed Won, say) won&apos;t appear until the
                      next nightly rebuild, even after Refresh.
                    </p>
                    {data.fetched_at && (
                      <p className="mt-1.5 text-muted-foreground">
                        Last pulled from Snowflake{" "}
                        {fmtDateTime(data.fetched_at)}.
                      </p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refresh.mutate()}
                disabled={refresh.isPending}
              >
                <RefreshCw
                  className={`mr-1.5 h-3.5 w-3.5 ${
                    refresh.isPending ? "animate-spin" : ""
                  }`}
                />
                {refresh.isPending ? "Refreshing…" : "Refresh"}
              </Button>
            </div>

            {/* DS FilterBar — Owner as a multi-select popover, opened/created
                and forecasted-close as date ranges. Matches the Open Deals
                filter UX; active selections render as removable pills. */}
            <DsFilterBar>
              <FilterChip
                label="Owner"
                count={ownerNames.length}
                valueLabel={
                  ownerNames.length === 1
                    ? ownerNames[0]
                    : ownerNames.length > 1
                    ? `${ownerNames.length} owners`
                    : undefined
                }
              >
                <CheckboxList
                  options={ownerOptions.map((o) => ({ value: o, label: o }))}
                  selected={ownerNames}
                  onChange={setOwnerNames}
                  searchable
                />
              </FilterChip>

              <FilterChip
                label="Stage"
                count={activeStages.length}
                valueLabel={
                  activeStages.length === 1
                    ? activeStages[0]
                    : activeStages.length > 1
                    ? `${activeStages.length} stages`
                    : undefined
                }
              >
                <CheckboxList
                  options={stageOptions}
                  selected={activeStages}
                  onChange={setStageNames}
                  searchable
                />
              </FilterChip>

              {/* Two side-by-side range pickers need visible group labels —
                  the DS picker only labels its start/end inputs. */}
              <span className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Opened
                </span>
                <DateRangePicker
                  value={createdRange}
                  onChange={setCreatedRange}
                  aria-label="Deal-opened date range"
                />
              </span>
              <span className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Closes
                </span>
                <DateRangePicker
                  value={closeRange}
                  onChange={setCloseRange}
                  aria-label="Forecasted-close date range"
                />
              </span>

              {ownerNames.map((o) => (
                <ActiveFilterPill
                  key={`ow-${o}`}
                  label={o}
                  onRemove={() =>
                    setOwnerNames(ownerNames.filter((x) => x !== o))
                  }
                />
              ))}
              {activeStages.map((s) => (
                <ActiveFilterPill
                  key={`st-${s}`}
                  label={s}
                  onRemove={() =>
                    setStageNames(stageNames.filter((x) => x !== s))
                  }
                />
              ))}
              {createdLabel && (
                <ActiveFilterPill
                  label={`Opened: ${createdLabel}`}
                  onRemove={() => setCreatedRange({ start: null, end: null })}
                />
              )}
              {closeLabel && (
                <ActiveFilterPill
                  label={`Close: ${closeLabel}`}
                  onRemove={() => setCloseRange({ start: null, end: null })}
                />
              )}

              {hasAnyFilter && <FilterClearAll onClick={clearAll} />}
            </DsFilterBar>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <KpiTile
                label="Open deals"
                value={fmtCount(tileCounts.total)}
                sub={`${fmtCents(tileCounts.cents)} pipeline`}
                onClick={() => setBand(null)}
                selected={band === null}
              />
              <KpiTile
                label="Healthy"
                value={fmtCount(tileCounts.healthy)}
                tone="positive"
                onClick={() => toggleBand("healthy")}
                selected={band === "healthy"}
              />
              <KpiTile
                label="At risk"
                value={fmtCount(tileCounts.at_risk)}
                tone="warning"
                onClick={() => toggleBand("at_risk")}
                selected={band === "at_risk"}
              />
              <KpiTile
                label="Stalled"
                value={fmtCount(tileCounts.stalled)}
                tone="danger"
                onClick={() => toggleBand("stalled")}
                selected={band === "stalled"}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
              <div className="xl:col-span-2">
                <DealLandscape
                  deals={deals}
                  selectedId={selected?.hubspot_deal_id ?? null}
                  onSelect={setSelected}
                />
              </div>
              <DealHealthDetail deal={selected} />
            </div>

            <DealHealthTable
              deals={deals}
              selectedId={selected?.hubspot_deal_id ?? null}
              onSelect={setSelected}
            />

            <p className="text-xs text-muted-foreground">
              Engagement is scored relative to the open deals in the same
              pipeline (emails + calls on the deal&apos;s company over the last
              90 days, inbound weighted double, decayed by silence). See the
              How To tab for the full formulas. Data from the nightly HubSpot
              mart, cached server-side for 4h
              {data.fetched_at && (
                <> · pulled from Snowflake {fmtDateTime(data.fetched_at)}</>
              )}
              .
            </p>
          </>
        )}
      </div>
    </div>
  );
}
